/**
 * PR78: Smart Print Local Drive Data Adapter Plan — Registry
 *
 * Metadata-only local-drive data plan for Smart Print.
 * No real file read/write.
 * No SQLite.
 * No backup/restore/import/export execution.
 * Deep-frozen so runtime mutation is impossible.
 */
// ---------------------------------------------------------------------------
// Deep freeze helper
// ---------------------------------------------------------------------------
export function deepFreeze(value) {
    if (value === null || typeof value !== "object")
        return value;
    Object.freeze(value);
    for (const key of Object.keys(value)) {
        const child = value[key];
        if (child !== null && typeof child === "object" && !Object.isFrozen(child)) {
            deepFreeze(child);
        }
    }
    return value;
}
// ---------------------------------------------------------------------------
// Shared safety constants
// ---------------------------------------------------------------------------
const COMMON_BLOCKED_ACTIONS = deepFreeze([
    "read_user_drive",
    "write_user_drive",
    "delete_user_file",
    "create_local_database",
    "run_sqlite",
    "create_backup",
    "restore_backup",
    "import_file",
    "export_file",
    "open_file_picker",
    "desktop_file_access",
    "browser_file_access",
    "network_call",
    "db_write",
    "supabase_call",
    "provider_call",
    "secret_read",
    "auto_save",
    "auto_delete",
    "payment_trigger",
    "role_mutation",
    "secret_mutation",
]);
const COMMON_FUTURE_PR_SCOPE = deepFreeze([
    "PR79: file read/write adapter with owner approval",
    "PR80: SQLite integration plan",
    "PR81: backup/restore runtime",
    "PR82: import/export runtime",
    "PR83: desktop file picker integration",
]);
// ---------------------------------------------------------------------------
// Storage area definitions
// ---------------------------------------------------------------------------
const COMPANY_PROFILE = deepFreeze({
    id: "company_profile",
    displayName: "Company Profile",
    description: "Company identity, GSTIN, address, and contact details.",
    relativePath: "companies/<company-id>/company.json",
    filePattern: "company.json",
    recommendedFormat: "json",
    containsPersonalData: false,
    containsFinancialData: false,
    requiresUserApproval: true,
    readAllowedInPr78: false,
    writeAllowedInPr78: false,
    deleteAllowedInPr78: false,
    backupRequired: true,
    restoreAllowedInPr78: false,
    importAllowedInPr78: false,
    exportAllowedInPr78: false,
    blockedActions: COMMON_BLOCKED_ACTIONS,
    futurePrScope: COMMON_FUTURE_PR_SCOPE,
});
const CUSTOMERS = deepFreeze({
    id: "customers",
    displayName: "Customers",
    description: "Customer master data including names, addresses, and GSTINs.",
    relativePath: "companies/<company-id>/customers/",
    filePattern: "customers.jsonl",
    recommendedFormat: "jsonl",
    containsPersonalData: true,
    containsFinancialData: false,
    requiresUserApproval: true,
    readAllowedInPr78: false,
    writeAllowedInPr78: false,
    deleteAllowedInPr78: false,
    backupRequired: true,
    restoreAllowedInPr78: false,
    importAllowedInPr78: false,
    exportAllowedInPr78: false,
    blockedActions: COMMON_BLOCKED_ACTIONS,
    futurePrScope: COMMON_FUTURE_PR_SCOPE,
});
const SUPPLIERS = deepFreeze({
    id: "suppliers",
    displayName: "Suppliers",
    description: "Supplier and vendor master data.",
    relativePath: "companies/<company-id>/suppliers/",
    filePattern: "suppliers.jsonl",
    recommendedFormat: "jsonl",
    containsPersonalData: true,
    containsFinancialData: false,
    requiresUserApproval: true,
    readAllowedInPr78: false,
    writeAllowedInPr78: false,
    deleteAllowedInPr78: false,
    backupRequired: true,
    restoreAllowedInPr78: false,
    importAllowedInPr78: false,
    exportAllowedInPr78: false,
    blockedActions: COMMON_BLOCKED_ACTIONS,
    futurePrScope: COMMON_FUTURE_PR_SCOPE,
});
const ITEMS = deepFreeze({
    id: "items",
    displayName: "Items",
    description: "Product and service item master data.",
    relativePath: "companies/<company-id>/items/",
    filePattern: "items.jsonl",
    recommendedFormat: "jsonl",
    containsPersonalData: false,
    containsFinancialData: false,
    requiresUserApproval: true,
    readAllowedInPr78: false,
    writeAllowedInPr78: false,
    deleteAllowedInPr78: false,
    backupRequired: true,
    restoreAllowedInPr78: false,
    importAllowedInPr78: false,
    exportAllowedInPr78: false,
    blockedActions: COMMON_BLOCKED_ACTIONS,
    futurePrScope: COMMON_FUTURE_PR_SCOPE,
});
const INVOICES = deepFreeze({
    id: "invoices",
    displayName: "Invoices",
    description: "Sales invoices, invoice items, and tax breakup.",
    relativePath: "companies/<company-id>/invoices/",
    filePattern: "invoices.jsonl",
    recommendedFormat: "jsonl",
    containsPersonalData: true,
    containsFinancialData: true,
    requiresUserApproval: true,
    readAllowedInPr78: false,
    writeAllowedInPr78: false,
    deleteAllowedInPr78: false,
    backupRequired: true,
    restoreAllowedInPr78: false,
    importAllowedInPr78: false,
    exportAllowedInPr78: false,
    blockedActions: COMMON_BLOCKED_ACTIONS,
    futurePrScope: COMMON_FUTURE_PR_SCOPE,
});
const QUOTATIONS = deepFreeze({
    id: "quotations",
    displayName: "Quotations",
    description: "Customer quotations and estimates.",
    relativePath: "companies/<company-id>/quotations/",
    filePattern: "quotations.jsonl",
    recommendedFormat: "jsonl",
    containsPersonalData: true,
    containsFinancialData: true,
    requiresUserApproval: true,
    readAllowedInPr78: false,
    writeAllowedInPr78: false,
    deleteAllowedInPr78: false,
    backupRequired: true,
    restoreAllowedInPr78: false,
    importAllowedInPr78: false,
    exportAllowedInPr78: false,
    blockedActions: COMMON_BLOCKED_ACTIONS,
    futurePrScope: COMMON_FUTURE_PR_SCOPE,
});
const PAYMENTS = deepFreeze({
    id: "payments",
    displayName: "Payments",
    description: "Payment records and receipts.",
    relativePath: "companies/<company-id>/payments/",
    filePattern: "payments.jsonl",
    recommendedFormat: "jsonl",
    containsPersonalData: false,
    containsFinancialData: true,
    requiresUserApproval: true,
    readAllowedInPr78: false,
    writeAllowedInPr78: false,
    deleteAllowedInPr78: false,
    backupRequired: true,
    restoreAllowedInPr78: false,
    importAllowedInPr78: false,
    exportAllowedInPr78: false,
    blockedActions: COMMON_BLOCKED_ACTIONS,
    futurePrScope: COMMON_FUTURE_PR_SCOPE,
});
const PRINT_TEMPLATES = deepFreeze({
    id: "print_templates",
    displayName: "Print Templates",
    description: "Invoice and label print templates.",
    relativePath: "companies/<company-id>/templates/",
    filePattern: "templates.json",
    recommendedFormat: "json",
    containsPersonalData: false,
    containsFinancialData: false,
    requiresUserApproval: true,
    readAllowedInPr78: false,
    writeAllowedInPr78: false,
    deleteAllowedInPr78: false,
    backupRequired: true,
    restoreAllowedInPr78: false,
    importAllowedInPr78: false,
    exportAllowedInPr78: false,
    blockedActions: COMMON_BLOCKED_ACTIONS,
    futurePrScope: COMMON_FUTURE_PR_SCOPE,
});
const APP_SETTINGS = deepFreeze({
    id: "app_settings",
    displayName: "App Settings",
    description: "Application settings, preferences, and UI state.",
    relativePath: "companies/<company-id>/settings/",
    filePattern: "settings.json",
    recommendedFormat: "json",
    containsPersonalData: false,
    containsFinancialData: false,
    requiresUserApproval: true,
    readAllowedInPr78: false,
    writeAllowedInPr78: false,
    deleteAllowedInPr78: false,
    backupRequired: true,
    restoreAllowedInPr78: false,
    importAllowedInPr78: false,
    exportAllowedInPr78: false,
    blockedActions: COMMON_BLOCKED_ACTIONS,
    futurePrScope: COMMON_FUTURE_PR_SCOPE,
});
const AUDIT_LOGS = deepFreeze({
    id: "audit_logs",
    displayName: "Audit Logs",
    description: "Immutable local audit trail for data changes.",
    relativePath: "companies/<company-id>/audit/",
    filePattern: "audit.jsonl",
    recommendedFormat: "jsonl",
    containsPersonalData: true,
    containsFinancialData: true,
    requiresUserApproval: true,
    readAllowedInPr78: false,
    writeAllowedInPr78: false,
    deleteAllowedInPr78: false,
    backupRequired: true,
    restoreAllowedInPr78: false,
    importAllowedInPr78: false,
    exportAllowedInPr78: false,
    blockedActions: COMMON_BLOCKED_ACTIONS,
    futurePrScope: COMMON_FUTURE_PR_SCOPE,
});
const BACKUP_SNAPSHOTS = deepFreeze({
    id: "backup_snapshots",
    displayName: "Backup Snapshots",
    description: "Planned backup snapshot metadata. No backup execution in PR78.",
    relativePath: "companies/<company-id>/backups/",
    filePattern: "snapshot-<timestamp>.json",
    recommendedFormat: "json",
    containsPersonalData: true,
    containsFinancialData: true,
    requiresUserApproval: true,
    readAllowedInPr78: false,
    writeAllowedInPr78: false,
    deleteAllowedInPr78: false,
    backupRequired: true,
    restoreAllowedInPr78: false,
    importAllowedInPr78: false,
    exportAllowedInPr78: false,
    blockedActions: COMMON_BLOCKED_ACTIONS,
    futurePrScope: COMMON_FUTURE_PR_SCOPE,
});
const IMPORT_EXPORT_STAGING = deepFreeze({
    id: "import_export_staging",
    displayName: "Import/Export Staging",
    description: "Planned staging area for import and export operations.",
    relativePath: "companies/<company-id>/import-export/",
    filePattern: "staging.json",
    recommendedFormat: "json",
    containsPersonalData: true,
    containsFinancialData: true,
    requiresUserApproval: true,
    readAllowedInPr78: false,
    writeAllowedInPr78: false,
    deleteAllowedInPr78: false,
    backupRequired: false,
    restoreAllowedInPr78: false,
    importAllowedInPr78: false,
    exportAllowedInPr78: false,
    blockedActions: COMMON_BLOCKED_ACTIONS,
    futurePrScope: COMMON_FUTURE_PR_SCOPE,
});
// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------
export const SMART_PRINT_LOCAL_DATA_AREAS = deepFreeze([
    COMPANY_PROFILE,
    CUSTOMERS,
    SUPPLIERS,
    ITEMS,
    INVOICES,
    QUOTATIONS,
    PAYMENTS,
    PRINT_TEMPLATES,
    APP_SETTINGS,
    AUDIT_LOGS,
    BACKUP_SNAPSHOTS,
    IMPORT_EXPORT_STAGING,
]);
export const SMART_PRINT_LOCAL_DATA_PLAN_REGISTRY = deepFreeze({
    version: "1.0.0",
    generatedInPr: 78,
    dataBoundary: "smart_print_local_drive_plan_pr78_no_execution",
    areas: SMART_PRINT_LOCAL_DATA_AREAS,
});
// ---------------------------------------------------------------------------
// Lookup helpers
// ---------------------------------------------------------------------------
export function getSmartPrintLocalDataAreas() {
    return SMART_PRINT_LOCAL_DATA_AREAS;
}
export function getSmartPrintLocalDataAreaById(id) {
    return SMART_PRINT_LOCAL_DATA_AREAS.find((a) => a.id === id);
}
export function getAllSmartPrintLocalDataAreaIds() {
    return SMART_PRINT_LOCAL_DATA_AREAS.map((a) => a.id);
}
//# sourceMappingURL=smartPrintLocalDataPlanRegistry.js.map